// dashboard/frontend/src/components/Campaign/WebContactImporter.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import api from '../../api/axios';

const CacheInfo = ({ url }) => {
    const [cacheInfo, setCacheInfo] = useState(null);

    useEffect(() => {
        const checkCache = async () => {
            if (!url) return;
            try {
                const response = await api.get('/campaign-contacts/cache-info/', { 
                    params: { url } 
                });
                setCacheInfo(response.data);
            } catch (error) {
                console.error('Error checking cache:', error);
            }
        };
        checkCache();
    }, [url]);

    if (!url || !cacheInfo) return null;

    return (
        <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
                Cache Status: {cacheInfo.exists ? (
                    <>
                        {cacheInfo.status}
                        {cacheInfo.is_blocked && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                This URL appears to be blocked/restricted
                            </Alert>
                        )}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Size: {(cacheInfo.size_kb).toFixed(1)}KB
                            <br />
                            Cached file: {cacheInfo.file_path}
                        </Typography>
                    </>
                ) : "Not cached"}
            </Typography>
        </Box>
    );
};

const WebContactImporter = ({ open, onClose, campaignId, onImportComplete }) => {
    const [url, setUrl] = useState('');
    const [description, setDescription] = useState('');
    const [importing, setImporting] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const [importType, setImportType] = useState('auto');

    const handleImport = async () => {
        try {
            setImporting(true);
            setError(null);
            
            const response = await api.post('/campaign-contacts/web-import/', {
                url,
                description,
                import_type: importType,
                campaign_id: campaignId
            });

            setResults(response.data);
            if (onImportComplete) {
                onImportComplete(response.data);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Import Contacts from Web</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        label="URL"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="Paste URL or HTML content"
                    />

                    {/* Add CacheInfo component here */}
                    <CacheInfo url={url} />

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Import Type</InputLabel>
                        <Select
                            value={importType}
                            onChange={(e) => setImportType(e.target.value)}
                            label="Import Type"
                        >
                            <MenuItem value="auto">Auto-detect</MenuItem>
                            <MenuItem value="teaming">Teaming Profiles</MenuItem>
                            <MenuItem value="directory">Directory Listing</MenuItem>
                            <MenuItem value="custom">Custom (with description)</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Description (for custom imports)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Describe the contact information you want to extract..."
                        sx={{ mb: 2 }}
                    />

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {importing && (
                        <Box sx={{ width: '100%', mb: 2 }}>
                            <LinearProgress />
                        </Box>
                    )}

                    {results && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Successfully imported {results.created} new contacts
                            {results.updated > 0 && ` (${results.updated} updated)`}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button 
                    variant="contained"
                    onClick={handleImport}
                    disabled={importing || !url}
                >
                    {importing ? 'Importing...' : 'Import'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WebContactImporter;